import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { isArray } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';

export interface ICellWrapperParams extends ICellRendererParams {
  className?: string;
  customLocator?: string;
  isExpandableColumn?: boolean;
  modelUpdated$?: Observable<boolean>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
  selector: 'aux-ag-cell-wrapper',
  styles: [],
  template: `
    <p [attr.auto-qa]="autoTestAttribute" [ngClass]="[cellClass]">
      {{ this.value }}
    </p>
  `,
})
export class AgCellWrapperComponent implements ICellRendererAngularComp {
  private cdr = inject(ChangeDetectorRef);

  autoTestAttribute!: string;
  cellClass!: string;
  currentIndex?: number;
  group!: boolean;
  modelUpdatedSubscription?: Subscription;
  params!: ICellWrapperParams;
  value!: number | string;

  agInit(params: ICellWrapperParams): void {
    this.params = params;
    const cellValue = this.params.valueFormatted || this.params.value;

    this.group = !!this.params.node.group;
    this.value = cellValue;

    this.cellClass = this.params.className || '';

    this.cellClass +=
      this.params.isExpandableColumn && !this.group
        ? ` ag-row-group-leaf-indent ag-row-group-indent-${this.params.node.level} `
        : '';

    if (typeof this.params?.colDef?.cellClass !== 'function') {
      this.cellClass += this.getClassName(this.params?.colDef?.cellClass || '');
    }

    this.cellClass += ' truncate';

    this.cellClass = this.cellClass.trim();

    if (params.customLocator || params.colDef?.field) {
      this.autoTestAttribute = this.getAutoTestAttribute(params);
    }

    if (params.modelUpdated$ && !this.modelUpdatedSubscription) {
      this.subscribeToModelUpdated(params.modelUpdated$);
    }
  }

  destroy(): void {
    if (this.modelUpdatedSubscription) {
      this.modelUpdatedSubscription.unsubscribe();
    }
  }

  getAutoTestAttribute(params: ICellWrapperParams): string {
    const isTotalCell = params.node.rowPinned === 'bottom';
    const field = params.colDef?.field || '';
    const fieldName = params.customLocator || field;

    this.currentIndex = params.node.rowIndex || 0;

    return isTotalCell ? `${fieldName}_total` : `${fieldName}_${this.currentIndex}`;
  }

  getClassName(cellClass: string | string[]): string {
    if (isArray(cellClass)) {
      return cellClass.reduce((accum, className) => {
        return `${accum} ${className}`;
      }, '');
    }

    return cellClass;
  }

  refresh(_params: ICellWrapperParams): boolean {
    return false;
  }

  subscribeToModelUpdated(modelUpdated$: Observable<boolean>) {
    this.modelUpdatedSubscription = modelUpdated$.subscribe(() => {
      if (this.autoTestAttribute && this.currentIndex !== this.params.node.rowIndex) {
        this.autoTestAttribute = this.getAutoTestAttribute(this.params);
        this.cdr.markForCheck();
      }
    });
  }
}

export const getWrapperCellOptions = (modelUpdated$?: Observable<boolean>): ColDef => {
  return {
    cellRenderer: AgCellWrapperComponent,
    cellRendererSelector: (params) => {
      const isGrouped = !!params.node.group;

      return {
        component: isGrouped ? 'agGroupCellRenderer' : AgCellWrapperComponent,
        params: { className: 'text-left', isExpandableColumn: true, modelUpdated$ },
      };
    },
  };
};
